import React, {useCallback, useContext, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {enUS, ru} from 'date-fns/locale';
import {default as BaseDatePicker} from 'react-datepicker';
import {FormFieldDesignContext} from '@src/contexts/FormFieldDesignContext';
import {IReduxStoreState} from '@src/@interfaces';
import {parseDate, stringifyDate} from '@src/helpers/dates';
import classnames from '@src/helpers/classnames';
import trans from '@src/helpers/trans';
import {DateTime} from '@src/@types/DateTime';

function CustomTimeInput(props: { date?: Date, value?: any, onChange?: (value: any) => void }) {
    const {value, onChange: baseOnChange} = props;
    const onChange = useCallback((e: React.FormEvent<HTMLInputElement>) => {
        if (baseOnChange) {
            baseOnChange(e.currentTarget.value);
        }
    }, [baseOnChange]);

    return <input
            type="time"
            value={value}
            onChange={onChange}
    />;
}

export default function DatePicker(props: {
    size?: 'sm',
    isInvalid?: boolean,
    value?: DateTime,
    label?: string,
    id?: string,
    onChange?: (datetime: DateTime | null) => void,
    onBlur?: Required<React.ComponentProps<typeof BaseDatePicker>>['onBlur'],
    className?: string,
    readOnly?: boolean,
    disabled?: boolean,
    showTimeInput?: boolean,
    showTimeSelect?: boolean,
    showPopperArrow?: boolean,
    dateFormat?: string,
    placeholderText?: string,
    highlightDates?: Required<React.ComponentProps<typeof BaseDatePicker>>['highlightDates'],
    inline?: boolean,
}) {
    const [design] = useContext(FormFieldDesignContext);
    const currentLocale = useSelector((state: IReduxStoreState) => state.locale.locale);

    const isInvalid = props.isInvalid;
    const size = props.size || design.size;
    const locale = {en: enUS, ru: ru}[currentLocale] ?? enUS;

    const rawValue = props.value;
    const baseOnChange = props.onChange;
    const [value, onChange] = useMemo(() => {
        const value = rawValue ? parseDate(rawValue).toDate() : undefined;

        const onChange = (date: Date | null) => {
            if (baseOnChange) {
                baseOnChange(date ? stringifyDate(date) as any : null);
            }
        };

        return [value, onChange];
    }, [baseOnChange, rawValue]);

    let placeholder = props.placeholderText;
    if (!placeholder && props.label) {
        placeholder = trans('Forms.enter_field', {
            field: trans(props.label, currentLocale).toLowerCase()
        }, currentLocale);
    }

    return <div className={classnames('custom-datepicker__wrapper', props.className, {'is-invalid': isInvalid})}>
        <BaseDatePicker
                selected={value}
                onChange={onChange}
                onBlur={props.onBlur}
                wrapperClassName={classnames({'is-invalid': isInvalid})}
                className={classnames('custom-datepicker', 'form-control', props.className, {
                    'is-invalid': isInvalid,
                    [`form-control-${size}`]: size,
                })}
                placeholderText={placeholder}
                readOnly={props.readOnly === true || design.fieldsAreReadOnly}
                disabled={props.disabled === true || design.fieldsAreDisabled}
                id={props.id}
                locale={locale}
                showTimeInput={props.showTimeInput}
                showTimeSelect={props.showTimeSelect}
                dateFormat={props.dateFormat}
                showPopperArrow={props.showPopperArrow}
                highlightDates={props.highlightDates}
                inline={props.inline}
                portalId="app"
                autoComplete="off"
                customTimeInput={<CustomTimeInput/>}
        />
    </div>;
}
